import * as React from "react";
import {
  Badge,
  Box,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate } from "react-router-dom";
import {
  useListUpdatePolling,
  type ListUpdateNotificationGroup,
  type ListUpdateNotificationItem,
} from "../../hooks/useListUpdatePolling";

/** localStorage：有新信息时是否自动展开面板 */
const AUTO_EXPAND_STORAGE_KEY = "gyb.listUpdate.autoExpandOnNew";

/**
 * 读取「有新信息自动展开」设置，默认开启。
 */
const readAutoExpandSetting = (): boolean => {
  try {
    const raw = window.localStorage.getItem(AUTO_EXPAND_STORAGE_KEY);
    if (raw === null) {
      return true;
    }
    return raw === "true";
  } catch {
    return true;
  }
};

interface StackedTypeCardProps {
  group: ListUpdateNotificationGroup;
  onOpen: (item: ListUpdateNotificationItem) => void;
}

/**
 * 同类型未读堆叠卡片：顶卡可点进详情，角标展示数量。
 */
const StackedTypeCard: React.FC<StackedTypeCardProps> = ({ group, onOpen }) => {
  const top = group.items[0];
  const count = group.items.length;
  const stackDepth = Math.min(count - 1, 2);

  if (!top) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", mb: stackDepth > 0 ? 1.5 : 0 }}>
      {Array.from({ length: stackDepth }).map((_, index) => {
        const depth = stackDepth - index;
        return (
          <Box
            key={`${group.type}-stack-${depth}`}
            sx={{
              position: "absolute",
              left: depth * 4,
              right: -depth * 2,
              top: depth * 4,
              height: "100%",
              borderRadius: 1.5,
              bgcolor: "grey.100",
              border: "1px solid",
              borderColor: "divider",
              zIndex: index,
            }}
          />
        );
      })}

      <Badge
        badgeContent={count}
        color="error"
        overlap="rectangular"
        sx={{
          width: "100%",
          display: "block",
          "& .MuiBadge-badge": {
            right: 10,
            top: 10,
            zIndex: 3,
          },
        }}
      >
        <Paper
          elevation={2}
          onClick={() => onOpen(top)}
          sx={{
            position: "relative",
            zIndex: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            cursor: "pointer",
            border: "1px solid",
            borderColor: "primary.light",
            bgcolor: "background.paper",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: 4,
            },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {group.typeLabel} · 点击进入详情
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, color: "text.primary", mt: 0.25 }}
          >
            {top.title}
          </Typography>
          {count > 1 ? (
            <Typography variant="caption" color="text.secondary">
              另有 {count - 1} 条同类型未读
            </Typography>
          ) : null}
        </Paper>
      </Badge>
    </Box>
  );
};

/**
 * 右上角铃铛消息入口：点击展开未读列表，支持「有新信息自动展开」。
 */
export const ListUpdateNotification: React.FC = () => {
  const navigate = useNavigate();
  const { groups, markAsRead } = useListUpdatePolling();
  const [open, setOpen] = React.useState(false);
  const [autoExpandOnNew, setAutoExpandOnNew] = React.useState(
    readAutoExpandSetting
  );
  const prevUnreadRef = React.useRef(0);

  const unreadCount = groups.reduce(
    (sum, group) => sum + group.items.length,
    0
  );

  React.useEffect(() => {
    const prev = prevUnreadRef.current;
    if (autoExpandOnNew && unreadCount > prev) {
      setOpen(true);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, autoExpandOnNew]);

  /**
   * 切换并持久化「有新信息自动展开」。
   */
  const handleAutoExpandChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setAutoExpandOnNew(checked);
    try {
      window.localStorage.setItem(
        AUTO_EXPAND_STORAGE_KEY,
        checked ? "true" : "false"
      );
    } catch {
      // 忽略本地存储失败
    }
  };

  /**
   * 点击堆叠顶卡：跳转详情并标记该条已读。
   */
  const handleOpenItem = (item: ListUpdateNotificationItem) => {
    markAsRead(item.id);
    setOpen(false);
    navigate(item.path);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        sx={{
          position: "fixed",
          top: 64,
          right: 16,
          zIndex: (theme) => theme.zIndex.modal,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          max={99}
        >
          <IconButton
            aria-label="最新消息"
            onClick={() => setOpen((prev) => !prev)}
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 3,
              "&:hover": {
                bgcolor: "grey.50",
              },
            }}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon color="primary" />
            ) : (
              <NotificationsNoneIcon />
            )}
          </IconButton>
        </Badge>

        {open ? (
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              width: 320,
              maxWidth: "calc(100vw - 32px)",
              overflow: "hidden",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "grey.900",
                color: "common.white",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>
                最新消息
              </Typography>
              {unreadCount > 0 ? (
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  未读 {unreadCount}
                </Typography>
              ) : null}
            </Stack>

            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoExpandOnNew}
                    onChange={handleAutoExpandChange}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    有新信息自动展开
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <Box sx={{ px: 1.5, py: 1.25, maxHeight: 360, overflowY: "auto" }}>
              {groups.length > 0 ? (
                <Stack spacing={1.25}>
                  {groups.map((group) => (
                    <StackedTypeCard
                      key={group.type}
                      group={group}
                      onOpen={handleOpenItem}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  暂无最新消息
                </Typography>
              )}
            </Box>
          </Paper>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
};
